/**
 * Real constellations, as stick figures a lesson's key ideas can take.
 *
 * Simplified versions of the traditional figures (the lines most star maps
 * draw), with star positions approximated from the sky and normalised to
 * roughly [-1, 1] with y pointing DOWN, as the canvas draws it. The first
 * star of each is its brightest (or its best-known): a lesson's most
 * important idea takes it.
 *
 * Grouped by star count so a lesson can take a figure that fits how many
 * key ideas it has (`pickSkyFigure`).
 */

export interface SkyFigure {
  name: string;
  /** One line about the real constellation, for the Constellations panel. */
  note: string;
  stars: ReadonlyArray<readonly [number, number]>;
  /** Pairs of star indices: the figure's lines. */
  lines: ReadonlyArray<readonly [number, number]>;
}

export const SKY_FIGURES: readonly SkyFigure[] = [
  {
    name: 'Triangulum',
    note: 'The Triangle: three faint stars, one of the smallest constellations, easy to spot as a simple wedge.',
    stars: [
      [0.85, 0.1],
      [-0.8, 0.45],
      [0.15, -0.55],
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 0],
    ],
  },
  {
    name: 'Crux',
    note: 'The Southern Cross: the smallest of all 88 constellations, and how sailors in the south found their way.',
    stars: [
      [0.1, 0.95], // Acrux
      [-0.7, 0.05], // Mimosa
      [0, -0.95], // Gacrux
      [0.65, -0.2], // Imai
    ],
    lines: [
      [0, 2],
      [1, 3],
    ],
  },
  {
    name: 'Aries',
    note: 'The Ram: a short bent line of stars, once the first sign of the zodiac.',
    stars: [
      [-0.2, -0.1], // Hamal
      [0.4, -0.2], // Sheratan
      [0.9, 0.1], // Mesarthim
      [-1, 0.35],
    ],
    lines: [
      [3, 0],
      [0, 1],
      [1, 2],
    ],
  },
  {
    name: 'Cassiopeia',
    note: 'The Queen: a bright W that circles the North Star all night long.',
    stars: [
      [-0.5, 0.3], // Schedar
      [-1, -0.3], // Caph
      [0, -0.1], // Gamma
      [0.5, 0.35], // Ruchbah
      [1, -0.25], // Segin
    ],
    lines: [
      [1, 0],
      [0, 2],
      [2, 3],
      [3, 4],
    ],
  },
  {
    name: 'Lyra',
    note: 'The Lyre: a small parallelogram hanging from Vega, one of the brightest stars in the sky.',
    stars: [
      [0, -1], // Vega
      [-0.2, -0.35], // Zeta
      [0.4, -0.2], // Delta
      [0.3, 0.75], // Sulafat
      [-0.35, 0.65], // Sheliak
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 1],
    ],
  },
  {
    name: 'Cygnus',
    note: 'The Swan, or the Northern Cross: a swan flying down the Milky Way.',
    stars: [
      [0, -1], // Deneb
      [0, -0.25], // Sadr
      [0, 0.35],
      [0, 1], // Albireo
      [-0.85, -0.45], // Gienah side
      [0.8, 0.05], // Gienah
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 3],
      [4, 1],
      [1, 5],
    ],
  },
  {
    name: 'Pegasus',
    note: "The winged horse: known for its Great Square, four stars that mark the horse's body.",
    stars: [
      [0.5, 0.5], // Markab
      [-0.5, 0.5], // Algenib
      [-0.5, -0.5], // Alpheratz
      [0.5, -0.5], // Scheat
      [0.85, 0.15], // Homam
      [1, 0.6], // Enif
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [0, 4],
      [4, 5],
    ],
  },
  {
    name: 'Ursa Major',
    note: 'The Great Bear: its seven brightest stars form the Big Dipper, whose edge points to the North Star.',
    stars: [
      [0.55, -0.25], // Dubhe
      [0.55, 0.15], // Merak
      [0.05, 0.2], // Phecda
      [-0.05, -0.15], // Megrez
      [-0.35, -0.25], // Alioth
      [-0.62, -0.25], // Mizar
      [-1, -0.1], // Alkaid
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [3, 4],
      [4, 5],
      [5, 6],
    ],
  },
  {
    name: 'Ursa Minor',
    note: 'The Little Bear: the Little Dipper, with the North Star at the end of its handle.',
    stars: [
      [-1, -0.3], // Polaris
      [-0.55, -0.2],
      [-0.15, -0.05],
      [0.2, 0.1],
      [0.75, 0.15], // Kochab
      [0.8, 0.6], // Pherkad
      [0.25, 0.55],
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
      [5, 6],
      [6, 3],
    ],
  },
  {
    name: 'Orion',
    note: 'The Hunter: three stars in a row make his belt, between the red giant Betelgeuse and blue Rigel.',
    stars: [
      [0.5, 0.7], // Rigel
      [-0.45, -0.75], // Betelgeuse
      [0.4, -0.65], // Bellatrix
      [-0.2, 0], // Alnitak
      [0, -0.05], // Alnilam
      [0.2, -0.1], // Mintaka
      [-0.35, 0.75], // Saiph
      [0, -1], // Meissa
    ],
    lines: [
      [1, 7],
      [7, 2],
      [1, 3],
      [2, 5],
      [3, 4],
      [4, 5],
      [3, 6],
      [5, 0],
    ],
  },
  {
    name: 'Leo',
    note: 'The Lion: a backwards question mark forms his mane, with Regulus at his heart.',
    stars: [
      [0.6, 0.6], // Regulus
      [0.55, 0.1], // Eta
      [0.65, -0.35], // Algieba
      [0.45, -0.75], // Adhafera
      [0.1, -0.85], // Rasalas
      [-0.45, -0.15], // Zosma
      [-1, 0.2], // Denebola
      [-0.45, 0.4], // Chertan
    ],
    lines: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [2, 5],
      [5, 6],
      [6, 7],
      [7, 0],
    ],
  },
  {
    name: 'Scorpius',
    note: 'The Scorpion: a long curving tail ending in a sting, with the red star Antares at its heart.',
    stars: [
      [-0.45, -0.35], // Antares
      [-0.95, -0.85],
      [-0.8, -0.6],
      [-0.95, -0.35],
      [-0.2, 0],
      [0, 0.4],
      [0.35, 0.7],
      [0.75, 0.65],
      [0.9, 0.3], // Shaula
    ],
    lines: [
      [1, 2],
      [3, 2],
      [2, 0],
      [0, 4],
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 8],
    ],
  },
];

/** A stable 0..1 from a string. */
function hash01(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

/**
 * The figure a lesson takes: the largest star count that fits within `max`
 * key ideas (so the figure is complete), one of that size chosen by the
 * lesson's id, avoiding any name in `avoid` (its neighbours') when another
 * of a fitting size is free. Deterministic: same lesson, same figure.
 */
export function pickSkyFigure(
  max: number,
  seed: string,
  avoid: ReadonlySet<string> = new Set()
): SkyFigure | null {
  const fitting = SKY_FIGURES.filter(f => f.stars.length <= max);
  if (fitting.length === 0) return null;
  const size = Math.max(...fitting.map(f => f.stars.length));
  // Prefer the biggest size; fall back to smaller ones only to avoid a
  // neighbour's figure.
  const bySize = [...new Set(fitting.map(f => f.stars.length))].sort(
    (a, b) => b - a
  );
  for (const s of [size, ...bySize.filter(x => x !== size)]) {
    const group = fitting.filter(f => f.stars.length === s);
    const start = Math.floor(hash01(seed) * group.length);
    for (let i = 0; i < group.length; i++) {
      const f = group[(start + i) % group.length];
      if (!avoid.has(f.name)) return f;
    }
  }
  const group = fitting.filter(f => f.stars.length === size);
  return group[Math.floor(hash01(seed) * group.length)];
}
